import * as React from 'react';
import { Button, FormGroup, FormLabel, Stack } from 'react-bootstrap';
import { General, ReferenceMultiSelect } from 'gossamer-front-end';
import { Field, FieldType, Filter, getFilter, ReferenceField } from 'gossamer-unified';
import { Event, EventUpload, InstitutionGroup } from 'cb-model';

export const groupsField: ReferenceField = {
  seq: 0,
  type: FieldType.Reference,
  label: 'Groups',
  refEntity: InstitutionGroup,
  description: 'groups to add this event to',
};

export interface TimetableFilterValues {
  title: string;
  type: string;
  dateFrom: string;
  dateTo: string;
  event_profile_id: string;
  location_type: string;
  location: string;
  groups: string[];
}

export const emptyFilters: TimetableFilterValues = {
  title: '',
  type: '',
  dateFrom: '',
  dateTo: '',
  event_profile_id: '',
  location_type: '',
  location: '',
  groups: [],
};

export const countActiveFilters = (filters: TimetableFilterValues): number =>
  (Object.keys(filters) as (keyof TimetableFilterValues)[]).filter((key) =>
    Array.isArray(filters[key]) ? filters[key].length > 0 : !!filters[key],
  ).length;

// local midnight at the start of a 'yyyy-mm-dd' date, plus a number of days
const localMidnight = (date: string, addDays = 0) => {
  const [year, month, day] = date.split('-').map((part) => parseInt(part, 10));
  return new Date(year, month - 1, day + addDays);
};

// the filters as query conditions on EventExtendedView - text fields match on contains (case-insensitive), dates are
// inclusive and an event's date is the local date of its start, and groups match if the event is in any of them
export const getFilterConditions = (filters: TimetableFilterValues): Filter[] => {
  const out: Filter[] = [];
  if (filters.title) {
    out.push(getFilter('title', 'ct', filters.title.trim()));
  }
  if (filters.type) {
    out.push(getFilter('type', 'eq', filters.type));
  }
  if (filters.dateFrom) {
    // start_at >= midnight
    out.push(getFilter('start_at', 'gt', new Date(localMidnight(filters.dateFrom).getTime() - 1).toISOString()));
  }
  if (filters.dateTo) {
    out.push(getFilter('start_at', 'lt', localMidnight(filters.dateTo, 1).toISOString()));
  }
  if (filters.event_profile_id) {
    out.push(getFilter('event_profile_id', 'eq', filters.event_profile_id));
  }
  if (filters.location_type) {
    out.push(getFilter('location_type', 'eq', filters.location_type));
  }
  if (filters.location) {
    out.push(getFilter('location', 'ct', filters.location.trim()));
  }
  if (filters.groups.length) {
    out.push(
      getFilter(
        'groups',
        'ia',
        filters.groups.map((id) => parseInt(id, 10)),
      ),
    );
  }
  return out;
};

// the value, once it has stopped changing for the given time - so that typing in a filter doesn't query on every key
export const useDebouncedValue = <T,>(value: T, delayMs: number): T => {
  const [debounced, setDebounced] = React.useState<T>(value);
  React.useEffect(() => {
    const timeout = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timeout);
  }, [value, delayMs]);
  return debounced;
};

// filter fields are never mandatory, so that options fields offer a blank choice
const asFilterField = (field: Field, label?: string): Field => ({
  ...field,
  mandatory: false,
  label: label || field.label,
  description: undefined,
});

const FILTER_FIELDS = {
  title: asFilterField(Event.fields.title, 'Title contains'),
  type: asFilterField(Event.fields.type),
  dateFrom: asFilterField(EventUpload.fields.event_date, 'From date'),
  dateTo: asFilterField(EventUpload.fields.event_date, 'To date'),
  event_profile_id: asFilterField(Event.fields.event_profile_id),
  location_type: asFilterField(Event.fields.location_type),
  location: asFilterField(Event.fields.location, 'Location contains'),
};

type SingleFilterKey = keyof typeof FILTER_FIELDS;

interface TimetableFiltersProps {
  filters: TimetableFilterValues;
  setFilters: (filters: TimetableFilterValues) => void;
}

export const TimetableFilters = (props: TimetableFiltersProps): React.JSX.Element => {
  const setFilter = <K extends keyof TimetableFilterValues>(key: K, value: TimetableFilterValues[K]) =>
    props.setFilters({ ...props.filters, [key]: value });

  const renderField = (key: SingleFilterKey) => (
    <FormGroup className="mb-2" controlId={`filter-${key}`}>
      <FormLabel>{FILTER_FIELDS[key].label}</FormLabel>
      <General
        editable={true}
        field={FILTER_FIELDS[key]}
        fieldId={`filter-${key}`}
        handleChange={(newValue) => setFilter(key, newValue || '')}
        value={props.filters[key]}
      />
    </FormGroup>
  );

  return (
    <div>
      {renderField('title')}
      {renderField('type')}
      <Stack direction="horizontal" gap={2}>
        {renderField('dateFrom')}
        {renderField('dateTo')}
      </Stack>
      {renderField('event_profile_id')}
      {renderField('location_type')}
      {renderField('location')}
      <FormGroup className="mb-2" controlId="filter-groups">
        <FormLabel>Groups (any of)</FormLabel>
        <ReferenceMultiSelect
          editable={true}
          field={groupsField}
          modalTitle="Add a Group"
          onChange={(newValue) => setFilter('groups', newValue)}
          value={props.filters.groups}
        />
      </FormGroup>
      <Button
        size="sm"
        variant="outline-secondary"
        disabled={countActiveFilters(props.filters) === 0}
        onClick={() => props.setFilters(emptyFilters)}
      >
        Clear filters
      </Button>
    </div>
  );
};
